// src/ChangePassword.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, CheckCircle, ArrowLeft, Shield } from 'lucide-react';

const ChangePassword = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const isManager = profile?.role === 'manager';

  const [isResetMode, setIsResetMode] = useState(
    sessionStorage.getItem('sqp_reset_mode') === '1'
  );

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsResetMode(true);
        sessionStorage.setItem('sqp_reset_mode', '1');
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [profiles, setProfiles] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [adminNewPassword, setAdminNewPassword] = useState('');
  const [showAdminNew, setShowAdminNew] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminSuccess, setAdminSuccess] = useState('');
  const [adminError, setAdminError] = useState('');

  useEffect(() => {
    if (isManager) {
      supabase.from('profiles').select('id, nom, email, role')
        .neq('role', 'manager').order('nom')
        .then(({ data }) => setProfiles(data || []));
    }
  }, [isManager]);

  const rules = [
    { label: 'Au moins 8 caractères', ok: newPassword.length >= 8 },
    { label: 'Une majuscule', ok: /[A-Z]/.test(newPassword) },
    { label: 'Une minuscule', ok: /[a-z]/.test(newPassword) },
    { label: 'Un chiffre', ok: /\d/.test(newPassword) },
  ];

  const isValid = rules.every(r => r.ok) && newPassword === confirmPassword &&
    (isResetMode || currentPassword.length > 0);

  const handleSubmit = async () => {
    setError('');
    if (newPassword !== confirmPassword) { setError('Les mots de passe ne correspondent pas.'); return; }
    if (!rules.every(r => r.ok)) { setError('Le mot de passe ne respecte pas les critères de sécurité.'); return; }
    setLoading(true);
    try {
      if (!isResetMode) {
        const { data: userData } = await supabase.auth.getUser();
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: userData?.user?.email, password: currentPassword,
        });
        if (signInError) { setError('Mot de passe actuel incorrect.'); setLoading(false); return; }
      }
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;
      sessionStorage.removeItem('sqp_reset_mode');
      setSuccess(true);
      setTimeout(() => navigate('/'), 2500);
    } catch (e) { setError('Erreur : ' + e.message); }
    setLoading(false);
  };

  const handleAdminReset = async () => {
    setAdminError(''); setAdminSuccess('');
    if (!selectedUserId) { setAdminError('Sélectionnez une commerciale.'); return; }
    if (adminNewPassword.length < 8) { setAdminError('Le mot de passe doit faire au moins 8 caractères.'); return; }
    setAdminLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://devis.takoussane.com/api';
      const res = await fetch(API_URL + '/admin/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: selectedUserId, password: adminNewPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur serveur');
      const p = profiles.find(p => p.id === selectedUserId);
      setAdminSuccess('Mot de passe de ' + (p?.nom || 'la commerciale') + ' réinitialisé avec succès.');
      setAdminNewPassword(''); setSelectedUserId('');
    } catch (e) { setAdminError('Erreur : ' + e.message); }
    setAdminLoading(false);
  };

  const InputField = ({ label, value, onChange, show, onToggle, placeholder }) => (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        <input type={show ? 'text' : 'password'} value={value}
          onChange={e => onChange(e.target.value)} placeholder={placeholder}
          style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb',
            borderRadius: '8px', padding: '10px 40px 10px 12px', fontSize: '14px', outline: 'none' }}
          onFocus={e => e.target.style.borderColor = '#3b82f6'}
          onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
        <button type="button" onClick={onToggle}
          style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af',
            display: 'flex', alignItems: 'center', padding: '2px' }}>
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );

  if (success) return (
    <div style={{ minHeight: '100vh', background: '#f0f4f8', display: 'flex',
      alignItems: 'center', justifyContent: 'center', fontFamily: 'Segoe UI, sans-serif' }}>
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        style={{ background: 'white', borderRadius: '16px', padding: '48px 40px',
          textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.1)', maxWidth: '400px' }}>
        <CheckCircle size={56} color="#10b981" style={{ marginBottom: '16px' }} />
        <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#1f2937', marginBottom: '8px' }}>
          Mot de passe modifié !
        </h2>
        <p style={{ color: '#6b7280', fontSize: '14px' }}>Redirection en cours...</p>
      </motion.div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4f8', fontFamily: 'Segoe UI, sans-serif' }}>
      <div style={{ background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
        padding: '16px 32px', display: 'flex', alignItems: 'center', gap: '16px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}>
        <button onClick={() => navigate(-1)}
          onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.28)'}
          onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.15)'}
          style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white',
            borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '14px',
            transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <ArrowLeft size={15} /> Retour
        </button>
        <h1 style={{ color: 'white', fontSize: '20px', fontWeight: 700, margin: 0 }}>
          🔒 Gestion des mots de passe
        </h1>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 24px',
        gap: '24px', flexWrap: 'wrap', alignItems: 'flex-start' }}>

        {/* Mon propre mot de passe */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          style={{ background: 'white', borderRadius: '16px', padding: '40px',
            width: '100%', maxWidth: '440px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>

          <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#1e40af', marginBottom: '20px',
            display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Lock size={18} /> Changer mon mot de passe
          </h2>

          <div style={{ background: '#f0f9ff', borderRadius: '10px', padding: '14px 16px',
            marginBottom: '28px', display: 'flex', alignItems: 'center', gap: '12px',
            border: '1px solid #bae6fd' }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '50%',
              background: '#1e40af', display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexShrink: 0 }}>
              <Lock size={17} color="white" />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e40af' }}>
                {profile?.nom || 'Mon compte'}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>{profile?.email || ''}</div>
            </div>
          </div>

          {!isResetMode && (
            <InputField label="Mot de passe actuel" value={currentPassword}
              onChange={setCurrentPassword} show={showCurrent}
              onToggle={() => setShowCurrent(!showCurrent)}
              placeholder="Votre mot de passe actuel" />
          )}

          <div style={{ height: '1px', background: '#f3f4f6', margin: '20px 0' }} />

          <InputField label="Nouveau mot de passe" value={newPassword}
            onChange={setNewPassword} show={showNew}
            onToggle={() => setShowNew(!showNew)} placeholder="Nouveau mot de passe" />

          {newPassword.length > 0 && (
            <div style={{ marginTop: '-8px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {rules.map((rule, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px',
                  fontSize: '12px', color: rule.ok ? '#10b981' : '#9ca3af' }}>
                  <span style={{ fontSize: '10px' }}>{rule.ok ? '✓' : '○'}</span>
                  {rule.label}
                </div>
              ))}
            </div>
          )}

          <InputField label="Confirmer le nouveau mot de passe" value={confirmPassword}
            onChange={setConfirmPassword} show={showConfirm}
            onToggle={() => setShowConfirm(!showConfirm)}
            placeholder="Répéter le nouveau mot de passe" />

          {confirmPassword.length > 0 && (
            <div style={{ marginTop: '-8px', marginBottom: '16px', fontSize: '12px',
              color: newPassword === confirmPassword ? '#10b981' : '#ef4444',
              display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>{newPassword === confirmPassword ? '✓' : '✗'}</span>
              {newPassword === confirmPassword ? 'Les mots de passe correspondent' : 'Les mots de passe ne correspondent pas'}
            </div>
          )}

          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: '8px', padding: '10px 14px', marginBottom: '16px',
              fontSize: '13px', color: '#ef4444' }}>
              ⚠️ {error}
            </div>
          )}

          <button onClick={handleSubmit} disabled={!isValid || loading}
            style={{ width: '100%', padding: '12px',
              background: isValid && !loading ? '#1e40af' : '#e5e7eb',
              color: isValid && !loading ? 'white' : '#9ca3af',
              border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 700,
              cursor: isValid && !loading ? 'pointer' : 'default', transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            {loading ? 'Modification en cours...' : '🔒 Modifier mon mot de passe'}
          </button>
        </motion.div>

        {/* Section Manager */}
        {isManager && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{ background: 'white', borderRadius: '16px', padding: '40px',
              width: '100%', maxWidth: '440px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
              border: '2px solid #e0e7ff' }}>

            <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#7c3aed', marginBottom: '6px',
              display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Shield size={18} /> Réinitialiser le mot de passe d'une commerciale
            </h2>
            <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '24px' }}>
              En tant que manager, vous pouvez réinitialiser le mot de passe de n'importe quelle commerciale sans connaître son mot de passe actuel.
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600,
                color: '#374151', marginBottom: '6px' }}>Commerciale</label>
              <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
                style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: '8px',
                  padding: '10px 12px', fontSize: '14px', outline: 'none', background: 'white', cursor: 'pointer' }}
                onFocus={e => e.target.style.borderColor = '#7c3aed'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}>
                <option value="">-- Sélectionner une commerciale --</option>
                {profiles.map(p => (
                  <option key={p.id} value={p.id}>{p.nom} — {p.email}</option>
                ))}
              </select>
            </div>

            <InputField label="Nouveau mot de passe" value={adminNewPassword}
              onChange={setAdminNewPassword} show={showAdminNew}
              onToggle={() => setShowAdminNew(!showAdminNew)}
              placeholder="Nouveau mot de passe pour cette commerciale" />

            {adminError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: '8px', padding: '10px 14px', marginBottom: '16px',
                fontSize: '13px', color: '#ef4444' }}>⚠️ {adminError}</div>
            )}

            {adminSuccess && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0',
                borderRadius: '8px', padding: '10px 14px', marginBottom: '16px',
                fontSize: '13px', color: '#15803d', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CheckCircle size={14} /> {adminSuccess}
              </div>
            )}

            <button onClick={handleAdminReset}
              disabled={!selectedUserId || adminNewPassword.length < 8 || adminLoading}
              style={{ width: '100%', padding: '12px',
                background: selectedUserId && adminNewPassword.length >= 8 && !adminLoading ? '#7c3aed' : '#e5e7eb',
                color: selectedUserId && adminNewPassword.length >= 8 && !adminLoading ? 'white' : '#9ca3af',
                border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 700,
                cursor: selectedUserId && adminNewPassword.length >= 8 && !adminLoading ? 'pointer' : 'default',
                transition: 'all 0.15s' }}>
              {adminLoading ? 'Réinitialisation...' : '🛡️ Réinitialiser le mot de passe'}
            </button>

            <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '12px', textAlign: 'center' }}>
              La commerciale devra utiliser ce nouveau mot de passe à sa prochaine connexion.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ChangePassword;